import { getOAuthReturnPath } from "./oauthReturnPath"

/**
 * Setting-up wizard navigation: React Router location.state only (no localStorage step keys).
 */

export const SETTINGUP_FROM_ONBOARDING_KEY = "settingup_from_onboarding"

export function markSettingUpFromOnboarding() {
  try {
    sessionStorage.setItem(SETTINGUP_FROM_ONBOARDING_KEY, "1")
  } catch {
    /* ignore */
  }
}

export function isSettingUpOnboardingSession() {
  try {
    return sessionStorage.getItem(SETTINGUP_FROM_ONBOARDING_KEY) === "1"
  } catch {
    return false
  }
}

/** OAuth callback landed on /settings/social-network but the connect started from onboarding. */
export function shouldRedirectSocialOAuthToSettingUp(search = "") {
  const params = new URLSearchParams(search)
  if (!params.get("token") && !params.get("code")) return false
  const returnPath = String(getOAuthReturnPath() || "")
  if (returnPath.includes("settingup")) return true
  return isSettingUpOnboardingSession()
}

/** @deprecated legacy keys — cleared on setting-up mount */
export const LEGACY_SETTING_UP_LOCAL_KEYS = [
  "currentStep",
  "selectedChannel",
  "selectedPageName",
]

export function clearLegacySettingUpLocalStorage() {
  try {
    for (const key of LEGACY_SETTING_UP_LOCAL_KEYS) {
      localStorage.removeItem(key)
    }
  } catch {
    /* ignore */
  }
}

/**
 * Router state after OAuth / calendar resume. Ephemeral — strip from history after apply.
 */
export function buildSettingUpLocationState({
  settingUpStep,
  resumeStep,
  oauthChannel,
  oauthPageName,
  onboardingData,
} = {}) {
  const state = {}
  if (typeof settingUpStep === "number" && settingUpStep >= 1 && settingUpStep <= 3) {
    state.settingUpStep = settingUpStep
  }
  if (typeof resumeStep === "number" && resumeStep >= 1 && resumeStep <= 3) {
    state.resumeStep = resumeStep
  }
  if (oauthChannel != null && String(oauthChannel).trim() !== "") {
    state.oauthChannel = String(oauthChannel)
  }
  if (oauthPageName != null && String(oauthPageName).trim() !== "") {
    state.oauthPageName = String(oauthPageName)
  }
  if (onboardingData != null) {
    state.onboardingData = onboardingData
  }
  return state
}

export function stripEphemeralSettingUpState(state) {
  if (!state || typeof state !== "object") return undefined
  const {
    settingUpStep: _s,
    resumeStep: _r,
    oauthChannel: _c,
    oauthPageName: _p,
    ...rest
  } = state
  return Object.keys(rest).length > 0 ? rest : undefined
}

/**
 * Apply location.state to setting-up UI. Returns true if any ephemeral field was consumed.
 */
export function readSettingUpLocationState(state) {
  const out = { step: null, oauthChannel: null, oauthPageName: null, consumed: false }
  if (!state || typeof state !== "object") return out

  if (typeof state.resumeStep === "number" && state.resumeStep >= 1 && state.resumeStep <= 3) {
    out.step = state.resumeStep
    out.consumed = true
  } else if (typeof state.settingUpStep === "number" && state.settingUpStep >= 1 && state.settingUpStep <= 3) {
    out.step = state.settingUpStep
    out.consumed = true
  }

  if (state.oauthChannel != null && String(state.oauthChannel).trim() !== "") {
    out.oauthChannel = String(state.oauthChannel)
    out.consumed = true
  }
  if (state.oauthPageName != null && String(state.oauthPageName).trim() !== "") {
    out.oauthPageName = String(state.oauthPageName)
    out.consumed = true
  }

  return out
}

export function navigateToSettingUpAfterOAuth(navigate, { step = 2, channel, pageName } = {}) {
  markSettingUpFromOnboarding()
  navigate("/settingup", {
    replace: true,
    state: buildSettingUpLocationState({
      settingUpStep: step,
      resumeStep: step,
      oauthChannel: channel,
      oauthPageName: pageName,
    }),
  })
}
